import { useEffect, useState } from 'react';
import { api } from '../lib/api';
import { useAuth } from '../lib/auth';

type Urgency = 'normal' | 'urgent' | 'critique';

type PendingDoc = {
  id: number;
  project_id: number;
  project_name: string;
  title: string;
  status: string;
  urgency: Urgency;
  deadline: string | null;
  updated_at: string;
};

type Task = {
  id: number;
  project_id: number;
  project_name: string;
  title: string;
  status: string;
  urgency: Urgency;
  deadline: string | null;
};

type DashboardData = {
  message_from_other: { from: string; text: string; at: string } | null;
  other_presence: { online: boolean; last_seen: string | null } | null;
  pending_docs: PendingDoc[];
  tasks: Task[];
  recently_validated: PendingDoc[];
};

const emptyData: DashboardData = {
  message_from_other: null,
  other_presence: null,
  pending_docs: [],
  tasks: [],
  recently_validated: [],
};

function formatDeadline(d: string | null) {
  if (!d) return '—';
  const date = new Date(d);
  const today = new Date();
  const diff = Math.round((date.getTime() - today.getTime()) / 86_400_000);
  if (diff < 0) return `en retard de ${-diff}j`;
  if (diff === 0) return "aujourd'hui";
  if (diff === 1) return 'demain';
  return `dans ${diff}j`;
}

function UrgencyBadge({ level }: { level: Urgency }) {
  return <span className={`badge badge-${level}`}>{level}</span>;
}

export default function Dashboard() {
  const { user } = useAuth();
  const [data, setData] = useState<DashboardData>(emptyData);
  const [messageDraft, setMessageDraft] = useState('');
  const [sending, setSending] = useState(false);

  useEffect(() => {
    api
      .get<DashboardData>('/dashboard')
      .then(setData)
      .catch(() => setData(emptyData));
  }, []);

  async function sendMessage() {
    if (!messageDraft.trim()) return;
    setSending(true);
    try {
      await api.post('/dashboard/message', { text: messageDraft.trim() });
      setMessageDraft('');
    } finally {
      setSending(false);
    }
  }

  async function actOnDoc(docId: number, action: 'valider' | 'revoir') {
    await api.post(`/documents/${docId}/${action}`);
    const refreshed = await api.get<DashboardData>('/dashboard');
    setData(refreshed);
  }

  return (
    <>
      <div className="topbar">
        <h1>Bonjour {user?.name?.split(' ')[0] ?? ''} 👋</h1>
        <input className="search" placeholder="Rechercher partout…" />
      </div>

      <div className="grid-2">
        <div className="card">
          <div className="card-title">Message pour l'autre</div>
          <div className="card-meta" style={{ marginBottom: 10 }}>
            Laisse un mot, une consigne, un encouragement.
          </div>
          <textarea
            rows={3}
            value={messageDraft}
            onChange={(e) => setMessageDraft(e.target.value)}
            placeholder="Écris ici…"
            style={{
              width: '100%',
              padding: 10,
              border: '1px solid var(--c-border)',
              borderRadius: 'var(--radius-sm)',
              background: 'var(--c-bg)',
              resize: 'vertical',
            }}
          />
          <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 8 }}>
            <button className="btn btn-primary" onClick={sendMessage} disabled={sending}>
              {sending ? 'Envoi…' : 'Envoyer'}
            </button>
          </div>
        </div>

        <div className="card">
          <div className="card-title">Côté de l'autre</div>
          {data.other_presence ? (
            <div className="card-meta">
              {data.other_presence.online
                ? '● en ligne'
                : `dernière visite : ${data.other_presence.last_seen ?? '—'}`}
            </div>
          ) : (
            <div className="card-meta">Aucune info de présence pour l'instant.</div>
          )}
          {data.message_from_other && (
            <div
              style={{
                marginTop: 12,
                padding: 12,
                background: 'var(--c-nav)',
                borderRadius: 'var(--radius-sm)',
              }}
            >
              <div className="card-meta" style={{ marginBottom: 4 }}>
                {data.message_from_other.from} — {data.message_from_other.at}
              </div>
              <div>{data.message_from_other.text}</div>
            </div>
          )}
        </div>
      </div>

      <div className="section-title">
        <h3>Documents en attente</h3>
        <span className="card-meta">{data.pending_docs.length}</span>
      </div>
      {data.pending_docs.length === 0 ? (
        <div className="card card-meta">Aucun document en attente. Respire.</div>
      ) : (
        <div className="grid-2">
          {data.pending_docs.map((d) => (
            <div key={d.id} className="card">
              <div
                style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}
              >
                <div className="card-meta">{d.project_name}</div>
                <UrgencyBadge level={d.urgency} />
              </div>
              <div className="card-title" style={{ marginTop: 6 }}>
                {d.title}
              </div>
              <div className="card-meta">Échéance : {formatDeadline(d.deadline)}</div>
              <div style={{ display: 'flex', gap: 8, marginTop: 12 }}>
                <button className="btn btn-voir">Voir</button>
                <button className="btn btn-valider" onClick={() => actOnDoc(d.id, 'valider')}>
                  Valider
                </button>
                <button className="btn btn-revoir" onClick={() => actOnDoc(d.id, 'revoir')}>
                  À revoir
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      <div className="section-title">
        <h3>Mes tâches</h3>
        <span className="card-meta">{data.tasks.length}</span>
      </div>
      {data.tasks.length === 0 ? (
        <div className="card card-meta">Rien à faire pour l'instant.</div>
      ) : (
        <div className="grid-2">
          {data.tasks.map((t) => (
            <div key={t.id} className="card">
              <div
                style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}
              >
                <div className="card-meta">{t.project_name}</div>
                <UrgencyBadge level={t.urgency} />
              </div>
              <div className="card-title" style={{ marginTop: 6 }}>
                {t.title}
              </div>
              <div className="card-meta">Échéance : {formatDeadline(t.deadline)}</div>
            </div>
          ))}
        </div>
      )}

      {data.recently_validated.length > 0 && (
        <>
          <div className="section-title">
            <h3>Récemment validés</h3>
            <span className="card-meta">cette semaine</span>
          </div>
          <div className="grid-2">
            {data.recently_validated.map((d) => (
              <div key={d.id} className="card">
                <div className="card-meta">{d.project_name}</div>
                <div className="card-title" style={{ marginTop: 6 }}>
                  ✓ {d.title}
                </div>
              </div>
            ))}
          </div>
        </>
      )}
    </>
  );
}
